import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { provisionTenant } from '../../sitePublishing/api';
import { ProvisionTenantResponse } from '../../sitePublishing/types';
import { createControlPlaneTenantUrl } from '../../tenancy/tenantUrls';
import { OnboardingPlanKey } from '../../sitePublishing/types';
import pluralize from 'pluralize';
import { provisioningHostname } from '../../tenancy/provisioningHostname';
import { BUSINESS_TYPES, suggestedServicesFor } from '../../sitePublishing/businessTypes';
import { TIMEZONES } from '../../constants';

const FOUNDER_PLANS: Array<{ key: OnboardingPlanKey; label: string }> = [
  { key: 'solo', label: 'Solo — 1 login' },
  { key: 'duo', label: 'Duo — 2 logins' },
  { key: 'trio', label: 'Trio — 3 logins' },
  { key: 'squad', label: 'Squad — 4 logins' },
  { key: 'studio', label: 'Studio — 5 logins' },
  { key: 'team', label: 'Team — up to 10 logins' },
  { key: 'enterprise', label: 'Enterprise — up to 25 logins' },
  { key: 'frontDesk', label: 'Front Desk — 1 admin and up to 8 resources' },
];

const slugify = (value: string): string => value.toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .slice(0, 63);

const ProvisionTenantPage: React.FC = () => {
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState('');
  const [slug, setSlug] = useState('');
  const [timezone, setTimezone] = useState('America/New_York');
  const [businessType, setBusinessType] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [planKey, setPlanKey] = useState<OnboardingPlanKey>('solo');
  const [resourceSingular, setResourceSingular] = useState('');
  const [result, setResult] = useState<ProvisionTenantResponse>();
  const hostname = useMemo(
    () => provisioningHostname(slug, process.env.REACT_APP_ENV),
    [slug]
  );
  const mutation = useMutation({
    mutationFn: () => provisionTenant({
      tenantId: slug,
      displayName,
      slug,
      timezone,
      businessType,
      ownerName,
      ownerEmail,
      hostname,
      planKey,
      ...(planKey === 'frontDesk' ? {
        resourceSingular: resourceSingular.trim(),
        resourcePlural: pluralize(resourceSingular.trim()),
      } : {}),
    }),
    onSuccess: setResult,
  });
  const invitationUrl = result ? createControlPlaneTenantUrl('/signup', result.tenantId, {
    code: result.ownerInviteCode,
  }) : '';
  const valid = Boolean(displayName.trim() && slug && businessType && ownerName.trim() && ownerEmail.trim() && timezone.trim() &&
    (planKey !== 'frontDesk' || resourceSingular.trim()));

  return (
    <Box p={{ xs: 2, md: 4 }}>
      <Paper sx={{ maxWidth: 760, mx: 'auto', p: { xs: 2, md: 4 } }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h4">Provision Website</Typography>
            <Typography color="text.secondary">Create an onboarding tenant, reserve its hostname, and generate the owner invitation.</Typography>
          </Box>
          {mutation.isError && <Alert severity="error">{mutation.error.message}</Alert>}
          {result ? (
            <>
              <Alert severity="success">{result.tenantId} is reserved in onboarding. It is not public until setup and publishing are complete.</Alert>
              <TextField label="Owner invitation" value={invitationUrl} InputProps={{ readOnly: true }} />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Button variant="contained" onClick={() => void navigator.clipboard.writeText(invitationUrl)}>Copy invitation</Button>
                <Button variant="outlined" onClick={() => window.location.assign(`/admin/superadmin/site?tenant=${encodeURIComponent(result.tenantId)}`)}>Edit starter site</Button>
                <Button onClick={() => navigate(`/admin/superadmin/manage/${result.tenantId}`)}>Manage tenant</Button>
              </Stack>
            </>
          ) : (
            <>
              <TextField label="Business display name" value={displayName} onChange={(event) => {
                setDisplayName(event.target.value);
                if (!slug) setSlug(slugify(event.target.value));
              }} />
              <TextField label="Slug / tenant ID" value={slug} helperText={hostname}
                onChange={(event) => setSlug(slugify(event.target.value))} />
              <TextField select required label="Business type" value={businessType}
                helperText={businessType ? `Suggested services: ${suggestedServicesFor(businessType).slice(0, 4).join(', ')}…` :
                  'Decides which services the owner is offered first.'}
                onChange={(event) => setBusinessType(event.target.value)}>
                {BUSINESS_TYPES.map((type) => <MenuItem key={type} value={type}>{type}</MenuItem>)}
              </TextField>
              <TextField select required label="Timezone" value={timezone}
                onChange={(event) => setTimezone(event.target.value)}>
                {TIMEZONES.map((zone) => <MenuItem key={zone} value={zone}>{zone}</MenuItem>)}
              </TextField>
              <TextField label="Owner name" value={ownerName} onChange={(event) => setOwnerName(event.target.value)} />
              <TextField label="Owner email" type="email" value={ownerEmail}
                helperText="We send the invitation here. The owner can sign up with any email."
                onChange={(event) => setOwnerEmail(event.target.value)} />
              <TextField select label="Plan" value={planKey}
                helperText="The owner will check out with this founder-selected plan."
                onChange={(event) => setPlanKey(event.target.value as OnboardingPlanKey)}>
                {FOUNDER_PLANS.map((plan) => <MenuItem key={plan.key} value={plan.key}>{plan.label}</MenuItem>)}
              </TextField>
              {planKey === 'frontDesk' ? <TextField required label="What do you call one bookable resource?"
                placeholder="e.g. Chair, Room, Bay, Vehicle"
                helperText={resourceSingular.trim() ? `Customers will see “${pluralize(resourceSingular.trim())}”.` : 'Use the singular name. You can add up to eight individual resources during setup.'}
                value={resourceSingular} onChange={(event) => setResourceSingular(event.target.value)} /> : null}
              <Alert severity="info">Provisioning does not deploy or change production DNS. The tenant stays in onboarding with a pending host.</Alert>
              <Button variant="contained" disabled={!valid || mutation.isPending} onClick={() => mutation.mutate()}>
                {mutation.isPending ? 'Provisioning…' : 'Provision tenant'}
              </Button>
            </>
          )}
        </Stack>
      </Paper>
    </Box>
  );
};

export default ProvisionTenantPage;
